import { ReactNode, useState, useEffect, useRef } from "react";
import C from "classnames";

import { useUserBanner } from "../../hooks/useUserBanner";
import { useOnlineStatus } from "../../../hooks";

import { UserBanner } from "../UserBanner/UserBanner";
import { OfflineBanner } from "../OfflineBanner/OfflineBanner";
import { UserNavBar, type UserNavBarProps } from "../UserNavBar/UserNavBar";
import { SkipLink } from "../../../components/SkipLink/SkipLink";
import S from "./Header.module.scss";

export interface HeaderProps {
  uiSettingsItems?: UserNavBarProps["uiSettingsItems"];
  breadcrumbs?: UserNavBarProps["breadcrumbs"];
  dialogTemplate?: UserNavBarProps["dialogTemplate"];
  fullWidth?: boolean;
  showBanner?: boolean;
  showOfflineBanner?: boolean;
  showNavBar?: boolean;
  children?: ReactNode;
  className?: string;
  testId?: string;
}

/**
 * Universal Header component for SJ UI Toolkit
 * Features:
 * - Fixed positioning
 * - Scroll background effect
 * - Accepts breadcrumbs, dialogTemplate, fullWidth, and custom children
 * - Used in KA, Learn, Champions, User profile layouts
 */
export function Header({
  uiSettingsItems,
  breadcrumbs,
  dialogTemplate,
  fullWidth = false,
  showBanner = true,
  showOfflineBanner = false,
  showNavBar = true,
  children,
  className,
  testId = "app-header",
}: HeaderProps) {
  const { banner, clearBanner } = useUserBanner();
  const isOnline = useOnlineStatus();
  const [isScrolled, setIsScrolled] = useState(false);
  const navbarRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    // Set --navbar-height CSS variable to the actual height of the navbar
    const root = document.documentElement;
    const navbarHeight = navbarRef.current ? navbarRef.current.offsetHeight : 0;
    root.style.setProperty("--navbar-height", `${navbarHeight}px`);
  }, [showBanner, banner]);

  return (
    <header className={C(S.header, isScrolled && S.headerScrolled, className)} data-testid={testId}>
      <SkipLink />
      {showOfflineBanner && !isOnline && <OfflineBanner />}
      {showBanner && banner && (
        <UserBanner banner={banner} handleClear={clearBanner} fullWidth={fullWidth} />
      )}
      {showNavBar && (
        <UserNavBar
          uiSettingsItems={uiSettingsItems}
          breadcrumbs={breadcrumbs}
          dialogTemplate={dialogTemplate}
          navbarRef={navbarRef}
        />
      )}
      {children}
    </header>
  );
}
